import React, { InputHTMLAttributes, TextareaHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";

// Input component
export interface ThemedInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Input size */
  inputSize?: "sm" | "md" | "lg";
}

export const ThemedInput = forwardRef<HTMLInputElement, ThemedInputProps>(
  ({ inputSize = "md", className, ...props }, ref) => {
    return (
      <input
        ref={ref}
        className={cn(
          // Base styles
          "w-full bg-white",
          "border-theme rounded-theme",
          // Focus styles
          "focus:ring-2 focus:ring-theme-primary focus:border-theme",
          "focus:outline-none",
          // Size styles
          inputSize === "sm" && "px-3 py-1.5 text-sm",
          inputSize === "md" && "px-4 py-2",
          inputSize === "lg" && "px-4 py-3 text-lg",
          // Disabled styles
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100",
          className
        )}
        {...props}
      />
    );
  }
);

ThemedInput.displayName = "ThemedInput";

// Textarea component
export interface ThemedTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Textarea size */
  inputSize?: "sm" | "md" | "lg";
}

export const ThemedTextarea = forwardRef<HTMLTextAreaElement, ThemedTextareaProps>(
  ({ inputSize = "md", className, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(
          // Base styles
          "w-full bg-white resize-none",
          "border-theme rounded-theme",
          // Focus styles
          "focus:ring-2 focus:ring-theme-primary focus:border-theme",
          "focus:outline-none",
          // Size styles
          inputSize === "sm" && "px-3 py-1.5 text-sm",
          inputSize === "md" && "px-4 py-2",
          inputSize === "lg" && "px-4 py-3 text-lg",
          // Disabled styles
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100",
          className
        )}
        {...props}
      />
    );
  }
);

ThemedTextarea.displayName = "ThemedTextarea";

// Checkbox wrapper component
export interface ThemedCheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Checkbox color when checked */
  colorClass?: string;
}

export const ThemedCheckbox = forwardRef<HTMLInputElement, ThemedCheckboxProps>(
  ({ colorClass = "bg-green-400", className, children, ...props }, ref) => {
    return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <div
          className={cn(
            "w-5 h-5 flex items-center justify-center",
            "border-theme rounded-theme",
            colorClass,
            className
          )}
        >
          <input
            ref={ref}
            type="checkbox"
            className="sr-only"
            {...props}
          />
          {props.checked && (
            <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        {children}
      </label>
    );
  }
);

ThemedCheckbox.displayName = "ThemedCheckbox";
